import React, { useState, useMemo } from 'react';
import Modal from './Modal';
import { useAuth } from '../context/AuthContext';
import './ProductionTiffExportModal.css';
import { getProductionDesignCode, loadProductionDesignFiles } from '../utils/productionDesignFile';

// Quantity buffer helper
function calculateModifiedQuantity(printQuantity, bufferMode = 'standard') {
  const qty = parseFloat(Number(printQuantity || 0).toFixed(2));
  let modifier = 0;

  if (bufferMode === 'standard') {
    if (qty <= 5) {
      modifier = 0.2;
    } else if (qty <= 15) {
      modifier = 0.4;
    } else if (qty < 100) {
      modifier = 0.6;
    } else if (qty < 1000) {
      modifier = 2.0;
    } else {
      modifier = 0;
    }
  } else if (bufferMode === 'legacy') {
    if (qty < 5) {
      modifier = 0.1;
    } else if (qty <= 10) {
      modifier = 0.3;
    } else {
      modifier = 0.5;
    }
  } else {
    modifier = 0; // 'exact'
  }

  return (qty + modifier).toFixed(2);
}

function sanitizeFolderName(name) {
  return String(name || '')
    .trim()
    .replace(/[<>:"/\\|?*]/g, '_')
    .replace(/\s+/g, ' ');
}

// Order Desk "print together" passes `items` (merged lines, one per SKU with the combined metres), `folderName`
// and `onRecord` (saves the print run instead of the single-batch tiff-run). Without them it works as before.
function ProductionTiffExportModal({ isOpen, onClose, batch, onRunSuccess, items = null, folderName = null, onRecord = null, subtitle = null }) {
  const { currentUser } = useAuth();
  const [sourceDirHandle, setSourceDirHandle] = useState(null);
  const [sourceDirName, setSourceDirName] = useState('');
  const [destDirHandle, setDestDirHandle] = useState(null);
  const [destDirName, setDestDirName] = useState('');

  // Fixed defaults: nested batch/fabric folders, standard buffer, skip unavailable items
  const folderStructure = 'nested';
  const bufferMode = 'standard';
  const includeUnavailable = false;

  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [results, setResults] = useState(null); // array of item statuses
  const [errorMsg, setErrorMsg] = useState('');
  const [copyReportMsg, setCopyReportMsg] = useState('');
  // idle | copying | saving | done | copy_failed | missing | save_failed
  const [phase, setPhase] = useState('idle');
  const [doneMsg, setDoneMsg] = useState('');

  const isFileSystemSupported = useMemo(() => {
    return typeof window !== 'undefined' && 'showDirectoryPicker' in window;
  }, []);

  const itemsToProcess = useMemo(() => {
    if (Array.isArray(items)) return items;
    if (!batch || !Array.isArray(batch.consolidatedItems)) return [];
    return batch.consolidatedItems.filter((it) => {
      if (!includeUnavailable && it?.status === 'unavailable') return false;
      return true;
    });
  }, [batch, includeUnavailable, items]);

  const handlePickSourceFolder = async () => {
    setErrorMsg('');
    try {
      const handle = await window.showDirectoryPicker({
        id: 'fvs-master-designs-source',
        mode: 'read',
      });
      setSourceDirHandle(handle);
      setSourceDirName(handle.name);
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error(err);
        setErrorMsg(`Failed to select source folder: ${err.message}`);
      }
    }
  };

  const handlePickDestFolder = async () => {
    setErrorMsg('');
    try {
      const handle = await window.showDirectoryPicker({
        id: 'fvs-production-batches-dest',
        mode: 'readwrite',
      });
      setDestDirHandle(handle);
      setDestDirName(handle.name);
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error(err);
        setErrorMsg(`Failed to select destination folder: ${err.message}`);
      }
    }
  };

  // Build a lookup index of all files in the source directory for instant case-insensitive matching
  const buildSourceIndex = async (dirHandle) => {
    const fileMap = new Map(); // normalized key -> FileSystemFileHandle
    for await (const entry of dirHandle.values()) {
      if (entry.kind === 'file') {
        const rawName = entry.name;
        // Normalize name: lowercase and strip extension
        const lower = rawName.toLowerCase();
        fileMap.set(lower, entry);

        // Also index without extension
        const dotIdx = lower.lastIndexOf('.');
        if (dotIdx > 0) {
          const baseName = lower.substring(0, dotIdx);
          if (!fileMap.has(baseName)) {
            fileMap.set(baseName, entry);
          }
        }
      }
    }
    return fileMap;
  };

  // Find matching file in source index
  const findMatchingFile = (sourceIndex, designCode, sku) => {
    if (!designCode && !sku) return null;

    const candidates = [];
    if (designCode) {
      const dLower = designCode.toLowerCase();
      candidates.push(
        `${dLower}.tif`,
        `${dLower}.tiff`,
        `${dLower}.jpg`,
        `${dLower}.jpeg`,
        `${dLower}.png`,
        `${dLower}.psd`,
        dLower
      );
    }
    if (sku) {
      const sLower = sku.toLowerCase();
      candidates.push(
        `${sLower}.tif`,
        `${sLower}.tiff`,
        `${sLower}.jpg`,
        `${sLower}.jpeg`,
        `${sLower}.png`,
        `${sLower}.psd`,
        sLower
      );
    }

    for (const cand of candidates) {
      if (sourceIndex.has(cand)) {
        return sourceIndex.get(cand);
      }
    }

    // Secondary partial matching if not found
    if (designCode) {
      const dLower = designCode.toLowerCase();
      for (const [key, handle] of sourceIndex.entries()) {
        if (key.startsWith(`${dLower}.`) || key === dLower) {
          return handle;
        }
      }
    }

    return null;
  };

  // Files this tool made earlier, e.g. "03 -- Pure Chiffon -- TA0738 --- 12.50 Meter.tif". A re-run deletes only these
  // (in this batch's folder and its fabric folders) before copying, so changed quantities or row order never leave a
  // second copy of a design for the printer. Anything else in the folder is left alone.
  const TOOL_FILE_RE = /^\d{2} -- .+ -- .+ --- \d+(\.\d+)? Meter\.tif$/i;
  const removeOldToolFiles = async (batchDir) => {
    let removed = 0;
    const clean = async (dir) => {
      const names = [];
      for await (const entry of dir.values()) if (entry.kind === 'file' && TOOL_FILE_RE.test(entry.name)) names.push(entry.name);
      for (const n of names) { await dir.removeEntry(n); removed += 1; }
    };
    await clean(batchDir);
    const subdirs = [];
    for await (const entry of batchDir.values()) if (entry.kind === 'directory') subdirs.push(entry);
    for (const sub of subdirs) {
      await clean(sub);
      let empty = true;
      for await (const _ of sub.values()) { empty = false; break; } // eslint-disable-line no-unused-vars
      if (empty) await batchDir.removeEntry(sub.name);
    }
    return removed;
  };

  const countOf = (list, status) => list.filter((r) => r.status === status).length;
  const summaryOf = (list, skipped = []) => ({
    total: list.length, copied: countOf(list, 'success'), missing: countOf(list, 'missing'), error: countOf(list, 'error'),
    ...(skipped.length ? { skipped } : {}),
  });

  // Server call for the single-batch run; the answer is always checked (a refused or failed save is never silent).
  const postTiffRun = async (body) => {
    const token = currentUser ? await currentUser.getIdToken() : '';
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(`/api/v9/production-batches/${batch.batchId}/tiff-run`, { method: 'POST', headers, body: JSON.stringify(body) });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.ok === false || data.success === false) {
      throw new Error(data?.error?.message || data?.message || `Server answered ${res.status}`);
    }
    return data;
  };
  // failed tries are logged too (countable); best effort, never hides the real error
  const logFailure = (stage, reason, list) => {
    if (!batch?.batchId) return;
    postTiffRun({ outcome: 'failed', stage, reason: String(reason || '').slice(0, 300), summary: list ? summaryOf(list) : null, together: !!onRecord }).catch(() => {});
  };

  // Mark the batch(es) "sent to printing". Only after this succeeds is the run a success.
  const saveRun = async (list, skipped = []) => {
    setPhase('saving');
    setErrorMsg('');
    try {
      const summary = summaryOf(list, skipped);
      if (onRecord) await onRecord(summary);
      else await postTiffRun({ outcome: 'sent', summary });
      setPhase('done');
      setDoneMsg(`Sent to printing: ${summary.copied} file${summary.copied === 1 ? '' : 's'} made${skipped.length ? `, ${skipped.length} design${skipped.length === 1 ? '' : 's'} left out (not found)` : ''}.`);
      if (typeof onRunSuccess === 'function') onRunSuccess();
    } catch (err) {
      setPhase('save_failed');
      setErrorMsg(`The files are ready, but the batch is NOT marked as sent to printing: ${err.message}. Press Try again (files are not copied again).`);
      logFailure('save', err.message, list);
    }
  };

  const handleStartProcessing = async () => {
    if (!sourceDirHandle) {
      setErrorMsg('Please select the Master Designs Source Folder.');
      return;
    }
    if (!destDirHandle) {
      setErrorMsg('Please select the Production Output Folder.');
      return;
    }
    if (itemsToProcess.length === 0) {
      setErrorMsg('No items in this batch to process.');
      return;
    }

    setIsProcessing(true);
    setPhase('copying');
    setErrorMsg('');
    setDoneMsg('');
    setResults(null);
    setProgress({ current: 0, total: itemsToProcess.length });

    let resultsList = [];
    try {
      // 1. Index source folder files
      const designOverrides = await loadProductionDesignFiles(itemsToProcess.map(item => item.sku), currentUser);
      const sourceIndex = await buildSourceIndex(sourceDirHandle);

      // 2. Prepare Batch Folder (an existing folder is reused); remove this tool's files from an earlier run
      const batchFolderName = sanitizeFolderName(folderName || batch.batchId || 'Batch');
      const batchDir = await destDirHandle.getDirectoryHandle(batchFolderName, { create: true });
      await removeOldToolFiles(batchDir);

      resultsList = [];

      for (let i = 0; i < itemsToProcess.length; i++) {
        const item = itemsToProcess[i];
        const rowNumber = String(i + 1).padStart(2, '0');
        const fabricName = item.fabricName || 'Fabric';
        const sku = String(item.sku || '').trim();
        const designCode = getProductionDesignCode(sku, designOverrides);
        const totalQty = parseFloat((item.totalQuantity || 0).toFixed(2));
        const modifiedQty = calculateModifiedQuantity(totalQty, bufferMode);

        const renameName = `${rowNumber} -- ${fabricName} -- ${designCode || 'NO_DESIGN'} --- ${modifiedQty} Meter.tif`;
        const sanitizedRename = sanitizeFolderName(renameName);

        const sourceFileHandle = findMatchingFile(sourceIndex, designCode, designOverrides[sku.toUpperCase()] ? '' : sku);

        if (!sourceFileHandle) {
          resultsList.push({
            rowNumber,
            fabricName,
            sku,
            designCode,
            totalQty,
            modifiedQty,
            renameName: sanitizedRename,
            status: 'missing',
            message: `TIFF file for design code "${designCode || sku}" not found in source folder.`,
          });
          setProgress({ current: i + 1, total: itemsToProcess.length });
          continue;
        }

        try {
          // Determine target directory (nested by fabric or flat)
          let targetFolder = batchDir;
          if (folderStructure === 'nested') {
            const fabricFolderName = sanitizeFolderName(fabricName);
            targetFolder = await batchDir.getDirectoryHandle(fabricFolderName, { create: true });
          }

          // Read source file and write to target (same name = replaced, never a second copy)
          const sourceFile = await sourceFileHandle.getFile();
          const targetFileHandle = await targetFolder.getFileHandle(sanitizedRename, { create: true });
          const writableStream = await targetFileHandle.createWritable();
          await writableStream.write(sourceFile);
          await writableStream.close();

          resultsList.push({
            rowNumber,
            fabricName,
            sku,
            designCode,
            totalQty,
            modifiedQty,
            renameName: sanitizedRename,
            sourceFileName: sourceFileHandle.name,
            status: 'success',
            message: `Copied as "${sanitizedRename}"`,
          });
        } catch (copyErr) {
          console.error(`Error copying ${sku}:`, copyErr);
          resultsList.push({
            rowNumber,
            fabricName,
            sku,
            designCode,
            totalQty,
            modifiedQty,
            renameName: sanitizedRename,
            sourceFileName: sourceFileHandle?.name,
            status: 'error',
            message: `Failed to copy: ${copyErr.message}`,
          });
        }

        setProgress({ current: i + 1, total: itemsToProcess.length });
      }

      setResults(resultsList);
    } catch (err) {
      console.error('Batch processing failed:', err);
      setResults(resultsList.length ? resultsList : null);
      setPhase('copy_failed');
      setErrorMsg(`Making the print files stopped: ${err.message}. Nothing is marked as sent to printing. Press Try again (files are made again and replace the old ones).`);
      logFailure('copy', err.message, resultsList);
      setIsProcessing(false);
      return;
    }

    const errors = countOf(resultsList, 'error');
    const missing = resultsList.filter((r) => r.status === 'missing');
    if (errors > 0) {
      setPhase('copy_failed');
      setErrorMsg(`${errors} file${errors === 1 ? '' : 's'} could not be copied. Nothing is marked as sent to printing. Press Try again (files are made again and replace the old ones).`);
      logFailure('copy', `${errors} copy errors`, resultsList);
    } else if (missing.length > 0) {
      setPhase('missing');
      setErrorMsg(`${missing.length} design${missing.length === 1 ? '' : 's'} not found in the source folder: ${missing.map((m) => m.designCode || m.sku).join(', ')}. Add them and press Try again, or send without them.`);
      logFailure('missing', `${missing.length} designs not found`, resultsList);
    } else {
      await saveRun(resultsList);
    }
    setIsProcessing(false);
  };

  const handleSendWithoutMissing = async () => {
    if (!results) return;
    setIsProcessing(true);
    await saveRun(results, results.filter((r) => r.status === 'missing').map((r) => r.designCode || r.sku));
    setIsProcessing(false);
  };
  const handleRetrySave = async () => {
    if (!results) return;
    setIsProcessing(true);
    await saveRun(results, results.filter((r) => r.status === 'missing').map((r) => r.designCode || r.sku));
    setIsProcessing(false);
  };

  const handleCopyMissingReport = () => {
    if (!results) return;
    const missing = results.filter((r) => r.status === 'missing' || r.status === 'error');
    if (missing.length === 0) return;

    const lines = [
      `Missing Designs in Batch: ${batch?.batchId}`,
      `Total missing: ${missing.length}`,
      '----------------------------------------',
      ...missing.map((m) => `SKU: ${m.sku} | Fabric: ${m.fabricName}`),
    ];

    navigator.clipboard.writeText(lines.join('\n')).then(() => {
      setCopyReportMsg('Copied missing designs list to clipboard!');
      setTimeout(() => setCopyReportMsg(''), 4000);
    });
  };

  const summary = useMemo(() => {
    if (!results) return null;
    const success = results.filter((r) => r.status === 'success').length;
    const missing = results.filter((r) => r.status === 'missing').length;
    const error = results.filter((r) => r.status === 'error').length;
    return { success, missing, error, total: results.length };
  }, [results]);

  return (
    <Modal isOpen={isOpen} onClose={isProcessing ? () => {} : onClose} contentClassName="tiff-export-modal">
      <div className="tiff-export-container">
        <div className="tiff-export-header">
          <div className="tiff-header-top-row">
            <div className="tiff-header-badge">Production File Automation</div>
            {batch?.tiffRunAt && (
              <span className="tiff-last-run-badge">
                Last Run: {new Date(batch.tiffRunAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </div>
          <h2>Prepare Production TIFF Files</h2>
          <p className="tiff-batch-subtitle">
            {subtitle || <>Batch: <strong>{batch?.batchId}</strong> ({batch?.batchQuality || 'Standard'})</>} &bull;{' '}
            {itemsToProcess.length} Items
          </p>
        </div>

        {!isFileSystemSupported ? (
          <div className="tiff-unsupported-alert">
            <h4>Browser Not Supported for Direct Local File Access</h4>
            <p>
              Your current browser does not support the Web File System Access API. Please open this app in{' '}
              <strong>Google Chrome</strong> or <strong>Microsoft Edge</strong> on Windows to directly create folders
              and copy design files.
            </p>
          </div>
        ) : (
          <>
            {/* Directory Selection */}
            <div className="tiff-section">
              <h3 className="tiff-section-title">Select Local Folders</h3>
              <div className="tiff-folders-grid">
                <div className="tiff-folder-card">
                  <div className="tiff-folder-info">
                    <span className="tiff-folder-label">Master Designs Source Folder (TIFF/JPG)</span>
                    <span className="tiff-folder-path" title={sourceDirName || 'Not Selected'}>
                      {sourceDirName ? `📁 ${sourceDirName}` : 'No folder selected'}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="tiff-folder-btn"
                    onClick={handlePickSourceFolder}
                    disabled={isProcessing}
                  >
                    {sourceDirName ? 'Change Folder' : 'Select Folder'}
                  </button>
                </div>

                <div className="tiff-folder-card">
                  <div className="tiff-folder-info">
                    <span className="tiff-folder-label">Production Output Destination Folder</span>
                    <span className="tiff-folder-path" title={destDirName || 'Not Selected'}>
                      {destDirName ? `📁 ${destDirName}` : 'No folder selected'}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="tiff-folder-btn"
                    onClick={handlePickDestFolder}
                    disabled={isProcessing}
                  >
                    {destDirName ? 'Change Folder' : 'Select Folder'}
                  </button>
                </div>
              </div>
            </div>

            {errorMsg && <div className="tiff-error-banner" role="alert">{errorMsg}</div>}
            {phase === 'done' && doneMsg && <div className="tiff-success-banner" role="status">{doneMsg}</div>}

            {/* Action & Progress */}
            <div className="tiff-action-area">
              {phase === 'save_failed' ? (
                <button type="button" className="tiff-primary-btn" onClick={handleRetrySave} disabled={isProcessing}>
                  {isProcessing ? 'Saving…' : 'Try again'}
                </button>
              ) : (
                <button
                  type="button"
                  className="tiff-primary-btn"
                  onClick={handleStartProcessing}
                  disabled={isProcessing || !sourceDirHandle || !destDirHandle}
                >
                  {phase === 'saving'
                    ? 'Marking as sent to printing…'
                    : isProcessing
                    ? `Processing (${progress.current}/${progress.total})...`
                    : ['copy_failed', 'missing'].includes(phase)
                    ? 'Try again'
                    : phase === 'done'
                    ? 'Make the files again'
                    : 'Start Copy & Rename for Production'}
                </button>
              )}
              {phase === 'missing' && (
                <button type="button" className="tiff-secondary-btn" onClick={handleSendWithoutMissing} disabled={isProcessing}>
                  Send without these {summary?.missing || 0}
                </button>
              )}
            </div>

            {isProcessing && (
              <div className="tiff-progress-bar-container">
                <div
                  className="tiff-progress-bar-fill"
                  style={{
                    width: `${progress.total > 0 ? (progress.current / progress.total) * 100 : 0}%`,
                  }}
                />
                <span className="tiff-progress-text">
                  Processing file {progress.current} of {progress.total}...
                </span>
              </div>
            )}

            {/* Results Summary & Table */}
            {results && (
              <div className="tiff-results-section">
                <div className="tiff-results-summary">
                  <div className="tiff-summary-stat success">
                    <span className="stat-count">{summary.success}</span>
                    <span className="stat-label">Copied</span>
                  </div>
                  <div className={`tiff-summary-stat ${summary.missing > 0 ? 'missing' : 'neutral'}`}>
                    <span className="stat-count">{summary.missing}</span>
                    <span className="stat-label">Missing Designs</span>
                  </div>
                  {summary.error > 0 && (
                    <div className="tiff-summary-stat error">
                      <span className="stat-count">{summary.error}</span>
                      <span className="stat-label">Errors</span>
                    </div>
                  )}
                  {summary.missing > 0 && (
                    <button
                      type="button"
                      className="tiff-copy-report-btn"
                      onClick={handleCopyMissingReport}
                    >
                      {copyReportMsg || '📋 Copy Missing Designs List'}
                    </button>
                  )}
                </div>

                <div className="tiff-table-wrapper">
                  <table className="tiff-results-table">
                    <thead>
                      <tr>
                        <th>Status</th>
                        <th>Row</th>
                        <th>Design Code</th>
                        <th>Fabric</th>
                        <th>SKU</th>
                        <th>Generated File Name</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.map((res, idx) => (
                        <tr
                          key={idx}
                          className={
                            res.status === 'missing'
                              ? 'row-missing'
                              : res.status === 'error'
                              ? 'row-error'
                              : 'row-success'
                          }
                        >
                          <td>
                            {res.status === 'success' && <span className="badge badge-success">✓ Copied</span>}
                            {res.status === 'missing' && <span className="badge badge-missing">⚠ Missing</span>}
                            {res.status === 'error' && <span className="badge badge-error">✕ Error</span>}
                          </td>
                          <td>{res.rowNumber}</td>
                          <td>
                            <strong>{res.designCode || '—'}</strong>
                          </td>
                          <td>{res.fabricName}</td>
                          <td className="mono-cell">{res.sku}</td>
                          <td className="mono-cell filename-cell" title={res.renameName}>
                            {res.renameName}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        <div className="tiff-footer">
          <button type="button" className="tiff-close-btn" onClick={onClose} disabled={isProcessing}>
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default ProductionTiffExportModal;
